import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MoneyInput } from "@/components/MoneyInput"; // 🆕 usa formato italiano 1.234,56 al posto di <Input type=number>

/**
 * CRUD inline editor per una lista lookup gestita lato server (per agenzia).
 * Endpoint: GET/POST/PUT/DELETE /api/lookup-lists/{listKey}
 *
 * Props:
 *  - listKey: string (es. "marital_status", "gender", "driving_type", "frazionamento")
 *  - title: string (titolo della sezione)
 *  - description: string (sottotitolo)
 *  - emoji: string (icona)
 *  - codePlaceholder, labelPlaceholder: placeholder input
 *  - labelMultiline: boolean — se true, usa <Textarea> invece di <Input> per l'etichetta (utile per template lunghi)
 *  - labelRows: numero di righe visibili quando labelMultiline
 */
export const LookupListEditor = ({ listKey, title, description, emoji = "📋", codePlaceholder = "es. codice", labelPlaceholder = "es. Etichetta", labelMultiline = false, labelRows = 4 }) => {
  const isCoverages = listKey === "coverages";
  const isInsuranceStatus = listKey === "insurance_status";
  const isBersaniSource = listKey === "bersani_source";
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ code: "", label: "", is_active: true, sort_order: 0, default_sum_insured: "", default_deductible: "", triggers_bersani: false, enables_other_attestato: false, opens_bersani_section: false, requires_other_plate: false });
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/lookup-lists/${listKey}`); setItems(data || []); }
    catch { /* silent */ }
  }, [listKey]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const code = (form.code || "").trim().replace(/[^A-Za-z0-9_-]/g, "_");
    if (!code) { toast.error("Codice obbligatorio"); return; }
    if (!form.label.trim()) { toast.error("Etichetta obbligatoria"); return; }
    const payload = {
      code,
      label: form.label.trim(),
      is_active: !!form.is_active,
      sort_order: Number(form.sort_order) || 0,
    };
    if (isCoverages) {
      payload.default_sum_insured = form.default_sum_insured === "" ? null : Number(form.default_sum_insured);
      payload.default_deductible = (form.default_deductible || "").trim() || null;
    }
    if (isInsuranceStatus) {
      payload.triggers_bersani = !!form.triggers_bersani;
      payload.enables_other_attestato = !!form.enables_other_attestato;
    }
    if (isBersaniSource) {
      payload.opens_bersani_section = !!form.opens_bersani_section;
      payload.requires_other_plate = !!form.requires_other_plate;
    }
    try {
      if (editingId) {
        await api.put(`/lookup-lists/${listKey}/${editingId}`, payload);
        toast.success("Voce aggiornata");
      } else {
        await api.post(`/lookup-lists/${listKey}`, payload);
        toast.success("Voce creata");
      }
      setForm({ code: "", label: "", is_active: true, sort_order: 0, default_sum_insured: "", default_deductible: "", triggers_bersani: false, enables_other_attestato: false, opens_bersani_section: false, requires_other_plate: false });
      setEditingId(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const edit = (it) => {
    setEditingId(it.id);
    setForm({
      code: it.code || "", label: it.label || "",
      is_active: it.is_active !== false, sort_order: it.sort_order || 0,
      default_sum_insured: it.default_sum_insured ?? "",
      default_deductible: it.default_deductible || "",
      triggers_bersani: !!it.triggers_bersani,
      enables_other_attestato: !!it.enables_other_attestato,
      opens_bersani_section: !!it.opens_bersani_section,
      requires_other_plate: !!it.requires_other_plate,
    });
  };

  const remove = async (it) => {
    if (!window.confirm(`Eliminare "${it.label}"?`)) return;
    try { await api.delete(`/lookup-lists/${listKey}/${it.id}`); toast.success("Voce eliminata"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 mt-6 p-5" data-testid={`lookup-${listKey}-section`}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">{emoji} {title}</h2>
          {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-3 p-3 bg-slate-50 rounded-md border border-slate-200">
        <div className="sm:col-span-3">
          <Label className="text-xs">Codice <span className="text-slate-400">(univoco)</span></Label>
          <Input
            data-testid={`lookup-${listKey}-form-code`}
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            placeholder={codePlaceholder}
            className="h-8 text-sm font-mono"
            disabled={!!editingId}
          />
        </div>
        <div className={labelMultiline ? "sm:col-span-12" : "sm:col-span-6"}>
          <Label className="text-xs">Etichetta visibile</Label>
          {labelMultiline ? (
            <Textarea
              data-testid={`lookup-${listKey}-form-label`}
              rows={labelRows}
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder={labelPlaceholder}
              className="text-sm font-mono"
            />
          ) : (
            <Input
              data-testid={`lookup-${listKey}-form-label`}
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder={labelPlaceholder}
              className="h-8 text-sm"
            />
          )}
        </div>
        <div className="sm:col-span-1">
          <Label className="text-xs">Ordine</Label>
          <Input
            data-testid={`lookup-${listKey}-form-order`}
            type="number"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
            className="h-8 text-sm"
          />
        </div>
        <div className="sm:col-span-2 flex items-end gap-1">
          <Button
            type="button"
            size="sm"
            className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white"
            onClick={submit}
            data-testid={`lookup-${listKey}-form-submit`}
          >
            {editingId ? "Salva" : "+ Aggiungi"}
          </Button>
          {editingId && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={() => { setEditingId(null); setForm({ code: "", label: "", is_active: true, sort_order: 0, default_sum_insured: "", default_deductible: "", triggers_bersani: false, enables_other_attestato: false, opens_bersani_section: false, requires_other_plate: false }); }}
            >
              X
            </Button>
          )}
        </div>
        {/* 🆕 Solo per `coverages`: campi default massimale/franchigia */}
        {isCoverages && (
          <>
            <div className="sm:col-span-6">
              <Label className="text-xs">Massimale predefinito (€) <span className="text-slate-400">opzionale — formato italiano es. 7.750.000,00</span></Label>
              <MoneyInput
                data-testid={`lookup-${listKey}-form-default-sum`}
                value={form.default_sum_insured}
                onChange={(v) => setForm({ ...form, default_sum_insured: v })}
                placeholder="es. 1.000.000,00"
                className="h-8"
              />
            </div>
            <div className="sm:col-span-6">
              <Label className="text-xs">Franchigia predefinita <span className="text-slate-400">opzionale (testo libero)</span></Label>
              <Input
                data-testid={`lookup-${listKey}-form-default-deductible`}
                value={form.default_deductible}
                onChange={(e) => setForm({ ...form, default_deductible: e.target.value })}
                placeholder='es. "500€" o "10%"'
                className="h-8 text-sm"
              />
            </div>
          </>
        )}
        {/* 🆕 Solo per `insurance_status`: toggle "attiva Bersani/RC Familiare" + toggle "Attiva attestato altro veicolo" */}
        {isInsuranceStatus && (
          <div className="sm:col-span-12 flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.triggers_bersani}
                onChange={(e) => setForm({ ...form, triggers_bersani: e.target.checked })}
                className="h-3.5 w-3.5 accent-emerald-600"
                data-testid={`lookup-${listKey}-form-triggers-bersani`}
              />
              ✨ Attiva la procedura Bersani / RC Familiare
            </label>
            <label className="inline-flex items-center gap-2 text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded px-2.5 py-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.enables_other_attestato}
                onChange={(e) => setForm({ ...form, enables_other_attestato: e.target.checked })}
                className="h-3.5 w-3.5 accent-sky-600"
                data-testid={`lookup-${listKey}-form-enables-other-attestato`}
              />
              📜 Attiva attestato altro veicolo
            </label>
          </div>
        )}
        {/* 🆕 Solo per `bersani_source`: toggles per comportamento voce */}
        {isBersaniSource && (
          <div className="sm:col-span-12 flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.opens_bersani_section}
                onChange={(e) => setForm({ ...form, opens_bersani_section: e.target.checked })}
                className="h-3.5 w-3.5 accent-emerald-600"
                data-testid={`lookup-${listKey}-form-opens-bersani-section`}
              />
              ✨ Apre la sezione Bersani / RC Familiare (familiare convivente)
            </label>
            <label className="inline-flex items-center gap-2 text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded px-2.5 py-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.requires_other_plate}
                onChange={(e) => setForm({ ...form, requires_other_plate: e.target.checked })}
                className="h-3.5 w-3.5 accent-sky-600"
                data-testid={`lookup-${listKey}-form-requires-other-plate`}
              />
              🚘 Richiede targa + dati veicolo di origine
            </label>
          </div>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-slate-500 italic px-2 py-3">Nessuna voce. I default verranno seedati alla prima apertura nel form.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid={`lookup-${listKey}-table`}>
            <thead>
              <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <th className="text-left px-3 py-2">Ordine</th>
                <th className="text-left px-3 py-2">Codice</th>
                <th className="text-left px-3 py-2">Etichetta</th>
                {isCoverages && <th className="text-right px-3 py-2">Massimale €</th>}
                {isCoverages && <th className="text-left px-3 py-2">Franchigia</th>}
                {isInsuranceStatus && <th className="text-center px-3 py-2">Bersani</th>}
                {isBersaniSource && <th className="text-center px-3 py-2">Comportamento</th>}
                <th className="text-center px-3 py-2">Attivo</th>
                <th className="text-right px-3 py-2">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`lookup-${listKey}-row-${it.id}`}>
                  <td className="px-3 py-2 text-slate-500 tabular-nums">{it.sort_order ?? 0}</td>
                  <td className="px-3 py-2 font-mono text-xs">{it.code}</td>
                  <td className={`px-3 py-2 ${labelMultiline ? "whitespace-pre-wrap text-xs max-w-[520px]" : ""}`}>{it.label}</td>
                  {isCoverages && (
                    <td className="px-3 py-2 text-right tabular-nums text-xs text-slate-700">
                      {it.default_sum_insured != null ? Number(it.default_sum_insured).toLocaleString("it-IT") : <span className="text-slate-300">—</span>}
                    </td>
                  )}
                  {isCoverages && (
                    <td className="px-3 py-2 text-xs text-slate-700">
                      {it.default_deductible || <span className="text-slate-300">—</span>}
                    </td>
                  )}
                  {isInsuranceStatus && (
                    <td className="px-3 py-2 text-center text-xs">
                      {it.triggers_bersani && <span className="inline-block bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5 mr-1" title="Attiva sezione Bersani/RC Familiare">✨ Bersani</span>}
                      {it.enables_other_attestato && <span className="inline-block bg-sky-100 text-sky-800 rounded px-1.5 py-0.5" title="Attiva campo Attestato altro veicolo">📜 Attestato</span>}
                      {!it.triggers_bersani && !it.enables_other_attestato && <span className="text-slate-300">—</span>}
                    </td>
                  )}
                  {isBersaniSource && (
                    <td className="px-3 py-2 text-center text-xs">
                      {it.opens_bersani_section && <span className="inline-block bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5 mr-1" title="Apre sezione Bersani">✨ Bersani</span>}
                      {it.requires_other_plate && <span className="inline-block bg-sky-100 text-sky-800 rounded px-1.5 py-0.5" title="Richiede targa altro veicolo">🚘 Targa</span>}
                      {!it.opens_bersani_section && !it.requires_other_plate && <span className="text-slate-300">—</span>}
                    </td>
                  )}
                  <td className="px-3 py-2 text-center">
                    {it.is_active !== false ? <span className="text-emerald-600">✓</span> : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => edit(it)}
                      className="text-xs text-blue-700 hover:underline mr-3"
                      data-testid={`lookup-${listKey}-edit-${it.id}`}
                    >Modifica</button>
                    <button
                      onClick={() => {
                        // 🆕 Duplica voce
                        setEditingId(null);
                        const baseCode = `${it.code}_copy`;
                        let candidate = baseCode;
                        let n = 2;
                        const existing = new Set(items.map((x) => x.code));
                        while (existing.has(candidate)) candidate = `${baseCode}_${n++}`;
                        setForm({
                          code: candidate,
                          label: `${it.label} (copia)`,
                          is_active: it.is_active !== false,
                          sort_order: (it.sort_order || 0) + 1,
                          default_sum_insured: it.default_sum_insured ?? "",
                          default_deductible: it.default_deductible || "",
                          triggers_bersani: !!it.triggers_bersani,
                          enables_other_attestato: !!it.enables_other_attestato,
                          opens_bersani_section: !!it.opens_bersani_section,
                          requires_other_plate: !!it.requires_other_plate,
                        });
                        toast.info("Form precompilato. Clicca '+ Aggiungi' per salvare la copia.");
                      }}
                      className="text-xs text-emerald-700 hover:underline mr-3"
                      data-testid={`lookup-${listKey}-duplicate-${it.id}`}
                    >Duplica</button>
                    <button
                      onClick={() => remove(it)}
                      className="text-xs text-red-700 hover:underline"
                      data-testid={`lookup-${listKey}-delete-${it.id}`}
                    >Elimina</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LookupListEditor;
